import { AlertColor, Autocomplete, Box, CircularProgress, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Control, Controller, FieldErrors, SubmitHandler, UseFormGetValues, UseFormHandleSubmit, UseFormReset, UseFormSetValue, UseFormTrigger } from "react-hook-form";
import { myStylePickers } from "../../../../../theme/buttons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { FormsErrors } from "../../../../../hooks/Forms/FormsErrors";
import { Payment } from "../../../../../interfaces/Purchases/Payments/payments";
import { Supplier, SupplierLocation } from "../../../../../interfaces/Purchases/Catalogs/suppliers";
import { useCurrencies } from "../../../../../hooks/useCurrencies";
import { GridValidRowModel } from "@mui/x-data-grid";
import { numberWithFourDecimalsRegex, numberWithTwoDecimalsRegex } from "../../../../../constants/Regex";
import { CompanyBankAccount } from "../../../../../interfaces/Security/companies";
import { SupplierBankAccount } from "../../../../../interfaces/Purchases/Catalogs/supplierBankAccounts";
import { Currency } from "../../../../../interfaces/Commons/currencies";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useFormatNumber } from "../../../../../hooks/useFormatNumber";
import { PaymentMethod } from "../../../../../interfaces/Sales/Catalogs/paymentMethods";
import { BankAccount } from "../../../../../interfaces/Commons/bankAccounts";
import { Divider } from "../../../../../components/Commons/Divider";
import { Consecutive } from "../../../../../interfaces/Commons/consecutives";
import GenericPromises from "../../../../../api/GenericPromises";

interface PaymentFormProps {
  handleSubmit: UseFormHandleSubmit<Payment, undefined>,
  onSubmit: SubmitHandler<Payment>,
  hasAdvanceSalesInvoice: boolean,
  mySupplierLocal: Supplier | undefined,
  defaultSupplierLocation: SupplierLocation | undefined,
  validateCurrencyConversion: () => boolean | 0 | undefined,
  control: Control<Payment, any>,
  errors: FieldErrors<Payment>,
  mySelectedRows: readonly GridValidRowModel[],
  onChangeTotals: (myRows: readonly GridValidRowModel[], currencySupplierBankAccount?: string, myTypingExchangeRate?: number) => Promise<void>,
  setValue: UseFormSetValue<Payment>,
  isEdit: boolean,
  comboCompanyBankAccounts: CompanyBankAccount[],
  getValues: UseFormGetValues<Payment>,
  comboSupplierBankAccounts: SupplierBankAccount[],
  exchangeRate: number,
  currencySupplier: Currency | undefined,
  setExchangeRate: Dispatch<SetStateAction<number>>,
  hasExchangeRate: boolean,
  comboSuppliersLocations: SupplierLocation[],
  setDefaultSupplierLocation: Dispatch<SetStateAction<SupplierLocation | undefined>>,
  comboPaymentMethods: PaymentMethod[],
  defaultPaymentMethod: PaymentMethod | undefined,
  isLoadingCurrenciesValues: boolean,
  defaultSupplierBankAccount: SupplierBankAccount | undefined,
  setDefaultSupplierBankAccount: Dispatch<SetStateAction<SupplierBankAccount | undefined>>,
  setCurrencySupplier: Dispatch<SetStateAction<Currency | undefined>>,
  trigger: UseFormTrigger<Payment>,
  defaultBankAccountFinal: BankAccount | undefined,
  comboCommissionCurrencies: Currency[],
  setCommissionCurrency: Dispatch<SetStateAction<Currency | undefined>>,
  comboBankAccountsGeneral: BankAccount[],
  setDefaultBankAccountFinal: Dispatch<SetStateAction<BankAccount | undefined>>,
  hasCommissionAmount: boolean,
  setHasCommissionAmount: Dispatch<SetStateAction<boolean>>,
  showSnackBar: (message: string, severity: AlertColor) => void,
  reset: UseFormReset<Payment>,
  setMyConsecutive: Dispatch<SetStateAction<Consecutive | undefined>>,
}

export const PaymentForm = ({
  handleSubmit,
  onSubmit,
  hasAdvanceSalesInvoice,
  mySupplierLocal,
  defaultSupplierLocation,
  validateCurrencyConversion,
  control,
  errors,
  mySelectedRows,
  onChangeTotals,
  setValue,
  isEdit,
  comboCompanyBankAccounts,
  getValues,
  comboSupplierBankAccounts,
  exchangeRate,
  currencySupplier,
  setExchangeRate,
  hasExchangeRate,
  comboSuppliersLocations,
  setDefaultSupplierLocation,
  comboPaymentMethods,
  defaultPaymentMethod,
  isLoadingCurrenciesValues,
  defaultSupplierBankAccount,
  setDefaultSupplierBankAccount,
  setCurrencySupplier,
  trigger,
  defaultBankAccountFinal,
  comboCommissionCurrencies,
  setCommissionCurrency,
  comboBankAccountsGeneral,
  setDefaultBankAccountFinal,
  hasCommissionAmount,
  setHasCommissionAmount,
  showSnackBar,
  reset,
  setMyConsecutive,
}: PaymentFormProps) => {
  const [t] = useTranslation("global");
  const { GetError } = FormsErrors();
  const { GetOperatorToGetDivisa } = useCurrencies();
  const { setFormatNumber } = useFormatNumber();
  const { GenericGetResource } = GenericPromises();
  const [openDatePicker, setOpenDatePicker] = useState(false);

  function roundToFourDecimals(num: number): number {
    return Math.round(num * 10000) / 10000;
  }

  const disableKeyboardEntry = (e: any) => {
    if (e?.preventDefault) {
      e?.preventDefault();
      e?.stopPropagation();
    }
  }

  useEffect(() => {
    const loadConsecutive = async () => {
      let responseConsecutives: any = await GenericGetResource(`/consecutives`)
        .catch((error) => {
          showSnackBar(error.message, "error")
        });
      let myConsecutivesCombo: Consecutive[] = await responseConsecutives.data.data;
      let myConsecutive: Consecutive | undefined = await myConsecutivesCombo.find((item) => item.resource_name === "cPayments" || item.resource_name === "EntyPayments" || item.resource_name === "EntyCPayments");
      setMyConsecutive(myConsecutive);

      GenericGetResource(`/consecutives/byConsecutiveId/${myConsecutive?.consecutive_id}`)
        .then((response) => {
          reset({
            payment_name: response.data.data[0].Consecutive,
          }, { keepValues: true });

          setValue("payment_name", response.data.data[0].Consecutive);
        })
        .catch((error) => {
          showSnackBar(error.message, "error");
        });
    }
    loadConsecutive();
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column custom-card-view w-100">
      <div className="d-flex flex-column w-100">
        {/* First row */}
        <Box display={"flex"} className="w-100" justifyContent={"space-between"}>
          <Box className="d-flex w-50">
            <Box className="w-50">
              <Typography style={{ fontWeight: "bold" }}>{t("suppliers.title-view")}:</Typography>
              <Typography>{mySupplierLocal?.tax_regime_code} - {mySupplierLocal?.tax_regime_description}</Typography>
            </Box>
            <Box className="w-50">
              <Typography>{defaultSupplierLocation?.business_name}</Typography>
              <Typography>{defaultSupplierLocation?.supplier_rfc}</Typography>
            </Box>
            <Box display={"flex"} flexDirection={"row"} sx={{ paddingRight: 1 }}>
              {hasAdvanceSalesInvoice &&
                <>
                  <Box sx={{ border: "solid 1px black", backgroundColor: "#FFFDE7", borderRadius: "3px" }} width={20} height={20} marginY={1} />
                  <Typography margin={1}>
                    {t("payments.info.advance-sales-invoice-selected")}
                  </Typography>
                </>
              }
            </Box>
          </Box>
          <Box className="d-flex w-50">
            <div className={`${validateCurrencyConversion() ? "w-50" : "w-100"}`}>
              <Controller
                name="total_amount"
                control={control}
                rules={{ required: true, min: hasAdvanceSalesInvoice ? undefined : 0.000001, pattern: numberWithTwoDecimalsRegex }}
                render={({ field }) => (
                  <TextField
                    variant="filled"
                    label={`${t("payments.fields.total_amount_payment")}`}
                    ref={field.ref}
                    value={field.value}
                    onChange={(event) => {
                      field.onChange(event.target.value);
                      if (validateCurrencyConversion()) {
                        let myFieldValue = event.target.value;
                        // get currency company
                        let myBankAccountCompany = comboCompanyBankAccounts.find((item) => item.company_bank_account_id === getValues("company_bank_account_id"));
                        let myCompanyCurrency = myBankAccountCompany?.currency_code;
                        // get currency supplier
                        let myBankAccountSuppplier = comboSupplierBankAccounts.find((item) => item.supplier_bank_account_id === getValues("supplier_bank_account_id"));
                        let myCurrencySupplier = myBankAccountSuppplier?.currency_code;
                        // calculated value and set to the other field
                        let myOperation = GetOperatorToGetDivisa(myCurrencySupplier ?? '', myCompanyCurrency ?? '');
                        switch (myOperation) {
                          case "multiply":
                            {
                              let myValue = roundToFourDecimals((Number(myFieldValue) || 0) * (exchangeRate ? Number(exchangeRate) : 1));
                              setValue("total_amount_related", myValue);
                              break;
                            }
                          case "divide":
                            {
                              let myValue = roundToFourDecimals((Number(myFieldValue) || 0) / (exchangeRate ? Number(exchangeRate) : 1));
                              setValue("total_amount_related", myValue);
                              break;
                            }
                          case "same":
                            {
                              let myValue = roundToFourDecimals(Number(myFieldValue) || 0);
                              setValue("total_amount_related", myValue);
                              break;
                            }
                        }
                      }
                      onChangeTotals(mySelectedRows, currencySupplier?.currency_code);
                    }}
                    error={!!errors.total_amount}
                    helperText={GetError(errors.total_amount?.type)}
                    size="small"
                    sx={{
                      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                      "& input[type=number]": { MozAppearance: "textfield", }, width: "100%", paddingRight: validateCurrencyConversion() ? 1 : 0
                    }}
                    type="number"
                    disabled={isEdit}
                  />
                )}
              />
            </div>
            {validateCurrencyConversion() &&
              <div className="w-50">
                <Controller
                  name="total_amount_related"
                  control={control}
                  rules={{ required: true, min: hasAdvanceSalesInvoice ? undefined : 0.000001, pattern: numberWithTwoDecimalsRegex }}
                  render={({ field }) => (
                    <TextField
                      variant="filled"
                      label={`${t("payments.fields.total_amount_related")}`}
                      ref={field.ref}
                      value={field.value}
                      onChange={(event) => {
                        field.onChange(event.target.value);
                        if (validateCurrencyConversion()) {
                          let myFieldValue = event.target.value;
                          // get currency company
                          let myBankAccountCompany = comboCompanyBankAccounts.find((item) => item.company_bank_account_id === getValues("company_bank_account_id"));
                          let myCompanyCurrency = myBankAccountCompany?.currency_code;
                          // get currency supplier
                          let myBankAccountSuppplier = comboSupplierBankAccounts.find((item) => item.supplier_bank_account_id === getValues("supplier_bank_account_id"));
                          let myCurrencySupplier = myBankAccountSuppplier?.currency_code;
                          // calculated value and set to the other field
                          let myOperation = GetOperatorToGetDivisa(myCompanyCurrency ?? '', myCurrencySupplier ?? '');
                          switch (myOperation) {
                            case "multiply":
                              {
                                let myValue = roundToFourDecimals((Number(myFieldValue) || 0) * (exchangeRate ? Number(exchangeRate) : 1));
                                setValue("total_amount", myValue);
                                break;
                              }
                            case "divide":
                              {
                                let myValue = roundToFourDecimals((Number(myFieldValue) || 0) / (exchangeRate ? Number(exchangeRate) : 1));
                                setValue("total_amount", myValue);
                                break;
                              }
                            case "same":
                              {
                                let myValue = roundToFourDecimals(Number(myFieldValue) || 0);
                                setValue("total_amount", myValue);
                                break;
                              }
                          }
                        }
                        onChangeTotals(mySelectedRows, currencySupplier?.currency_code);
                      }}
                      error={!!errors.total_amount}
                      helperText={GetError(errors.total_amount?.type)}
                      size="small"
                      sx={{
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                        "& input[type=number]": { MozAppearance: "textfield", }, width: "100%", paddingRight: 0
                      }}
                      type="number"
                      disabled={isEdit}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </div>
            }
          </Box>
        </Box>
        <Divider />
        {/* Second row */}
        <div className="d-flex w-100">
          <div className="d-flex w-50">
            <div className="w-50">
              <Controller
                name="payment_name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    variant="filled"
                    label={`${t("payments.fields.payment_name")}`}
                    ref={field.ref}
                    value={field.value}
                    onChange={(event) => { field.onChange(event.target.value) }}
                    error={!!errors.payment_name}
                    helperText={GetError(errors.payment_name?.type)}
                    size="small"
                    sx={{ width: "100%", paddingRight: 1 }}
                    InputProps={{ readOnly: true, disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    disabled={isEdit}
                  />
                )}
              />
            </div>
            <div className="w-50">
              <Controller
                name="payment_date"
                control={control}
                rules={{}}
                render={({ field }) =>
                  <DatePicker
                    open={openDatePicker}
                    onClose={() => setOpenDatePicker(false)}
                    ref={field.ref}
                    label={`${t("payments.fields.payment_date")}`}
                    onChange={(_: any) => { field.onChange(_); onChangeTotals(mySelectedRows, currencySupplier?.currency_code) }}
                    slotProps={{
                      popper: {
                        sx: myStylePickers
                      },
                      textField: {
                        variant: "filled",
                        size: "small",
                        sx: { width: "100%", paddingRight: 1 },
                        onClick: () => setOpenDatePicker(true),
                        onBeforeInput: disableKeyboardEntry,
                        error: !!errors.payment_date,
                        helperText: GetError(errors.payment_date?.type),
                      }
                    }}
                    defaultValue={dayjs()}
                    format="DD/MM/YYYY"
                    disabled={isEdit}
                  />
                }
              />
            </div>
          </div>
          <div className="d-flex w-50">
            <div className="w-50">
              <TextField
                variant="filled"
                label={`${t("payments.fields.exchange_rate")}`}
                size="small"
                sx={{
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                  "& input[type=number]": { MozAppearance: "textfield", }, width: "100%", paddingRight: 1
                }}
                value={exchangeRate}
                onChange={async (event) => {
                  let value = event.target.value;
                  value = value.replace(/[^0-9.]/g, '');
                  const parts = value.split('.');
                  if (parts.length > 2) {
                    value = `${parts[0]}.${parts.slice(1).join('')}`;
                  }
                  if (parts[1]?.length > 6) {
                    value = `${parts[0]}.${parts[1].substring(0, 6)}`;
                  }
                  const decimalValue = parseFloat(value);
                  await onChangeTotals(mySelectedRows, currencySupplier?.currency_code, decimalValue);
                  setExchangeRate(decimalValue);
                }}
                type="number"
                disabled={isEdit ? isEdit : !hasExchangeRate}
              />
            </div>
            <div className="w-50">
              <TextField
                sx={{ width: "100%" }}
                disabled={true}
                size="small"
                label={`${t("currencies.title-view")}`}
                variant="filled"
                value={`${currencySupplier?.currency_code} - ${currencySupplier?.currency_description}`}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex w-100">

        <div className="d-flex w-100 mt-2">
          <div className="w-50">
            <Controller
              name="supplier_business_name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Autocomplete
                  ref={field.ref}
                  size="small"
                  sx={{ width: "100%", paddingRight: 1 }}
                  options={comboSuppliersLocations}
                  defaultValue={defaultSupplierLocation}
                  getOptionLabel={(option) => `${option.business_name} - ${option.street}, ${option.city_name}, ${option.state_name}, ${option.country_name}`}
                  renderOption={(props, option: SupplierLocation) => (
                    <div key={option.supplier_location_id}>
                      <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                        {option.business_name} - {option.street}, {option.city_name}, {option.state_name}, {option.country_name}
                      </Box>
                    </div>
                  )}
                  isOptionEqualToValue={(option, value) => option.supplier_location_id === value.supplier_location_id}
                  onChange={(_, values) => {
                    setDefaultSupplierLocation(values ?? undefined);
                    field.onChange(values?.business_name || null);
                  }}
                  disableClearable
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`${t("purchaseinvoices.fields.address")}`}
                      variant="filled"
                      value={field.value}
                      error={!!errors.supplier_business_name}
                      helperText={GetError(errors.supplier_business_name?.type)}
                    />
                  )}
                  disabled={isEdit}
                />
              )}
            />
          </div>
          <div className="w-50">
            <Controller
              name="payment_method_id"
              control={control}
              rules={{ required: true }}
              render={({ field }) =>
                <Autocomplete
                  ref={field.ref}
                  size="small"
                  options={comboPaymentMethods}
                  defaultValue={defaultPaymentMethod}
                  getOptionLabel={(option) => `${option.code} - ${option.name}`}
                  renderOption={(props, option: PaymentMethod) => (
                    <div key={option.payment_method_id}>
                      <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                        {option.code} - {option.name}
                      </Box>
                    </div>
                  )}
                  sx={{ width: "100%", paddingRight: 1 }}
                  isOptionEqualToValue={(option, value) => option.payment_method_id === value.payment_method_id}
                  onChange={(_, values) => {
                    field.onChange(values?.payment_method_id || null)
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`${t("paymentmethods.title-view")}`}
                      variant="filled"
                      value={field.value}
                      error={!!errors.payment_method_id}
                      helperText={GetError(errors.payment_method_id?.type)}
                    />
                  )}
                  disabled={isEdit}
                />
              }
            />
          </div>
        </div>
        <div className="d-flex flex-row w-100">
          <div className="w-50">
            <Controller
              name="selected_amount"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  variant="filled"
                  label={`${t("payments.fields.selected_amount")}`}
                  ref={field.ref}
                  value={setFormatNumber({ value: field.value ?? 0 })}
                  error={!!errors.selected_amount}
                  helperText={GetError(errors.selected_amount?.type)}
                  size="small"
                  disabled={isEdit}
                  sx={{
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                    "& input[type=number]": { MozAppearance: "textfield", }, width: "100%", marginTop: 1, paddingRight: 1
                  }}
                  InputProps={{
                    endAdornment: (
                      <>
                        {isLoadingCurrenciesValues ? <CircularProgress color="primary" size={25} /> : null}
                      </>
                    )
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </div>
          <div className="w-50">
            <Controller
              name="difference_amount"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  variant="filled"
                  label={`${t("payments.fields.difference_amount")}`}
                  ref={field.ref}
                  value={setFormatNumber({ value: field.value ?? 0 })}
                  error={!!errors.difference_amount}
                  helperText={GetError(errors.difference_amount?.type)}
                  size="small"
                  disabled={isEdit}
                  sx={{
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                    "& input[type=number]": { MozAppearance: "textfield", }, width: "100%", marginTop: 1,
                    '& .MuiInputBase-input': {
                      color: (field.value && field.value <= 0) ? 'red' : 'black',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <>
                        {isLoadingCurrenciesValues ? <CircularProgress color="primary" size={25} /> : null}
                      </>
                    )
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </div>
        </div>
      </div>
      <Divider />
      <Box className="d-flex w-100 flex-column">
        <Box display={"flex"} className="w-100">
          <Box sx={{ width: "50%" }} className="d-flex custom-card-view">
            <Box className="w-100 d-flex flex-column">
              <Typography fontWeight={"bold"} fontSize={20} marginRight={1}>
                {t("purchaseinvoices.fields.account_deposit")}:
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography fontFamily={"sans-serif"} sx={{ display: "flex" }} paddingRight={1}>
                  <Typography fontWeight={"bold"} marginRight={1}>
                    {t("bankaccounts.fields.account_alias")}:
                  </Typography>
                  {defaultSupplierBankAccount?.account_alias}
                </Typography>
                {/* <Typography fontFamily={"sans-serif"} sx={{ display: "flex" }} paddingRight={1}>
                <Typography fontWeight={"bold"} marginRight={1}>
                  {t("bankaccounts.fields.bank_references")}:
                </Typography>
                {defaultSupplierBankAccount?.bank_references}
              </Typography> */}
                <Box sx={{ display: "flex" }}>
                  <Typography fontFamily={"sans-serif"} sx={{ display: "flex" }} width={"50%"} paddingRight={1}>
                    <Typography fontWeight={"bold"} marginRight={1}>
                      {t("bankaccounts.fields.bank_name")}:
                    </Typography>
                    {defaultSupplierBankAccount?.bank_name}
                  </Typography>
                  <Typography fontFamily={"sans-serif"} sx={{ display: "flex" }} paddingRight={1}>
                    <Typography fontWeight={"bold"} marginRight={1}>
                      {t("currencies.title-view")}:
                    </Typography>
                    {defaultSupplierBankAccount?.currency_description} - {defaultSupplierBankAccount?.currency_code}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex" }}>
                  <Typography fontFamily={"sans-serif"} sx={{ display: "flex" }} width={"50%"} paddingRight={1}>
                    <Typography fontWeight={"bold"} marginRight={1}>
                      {t("bankaccounts.fields.card_number")}:
                    </Typography>
                    {defaultSupplierBankAccount?.card_number}
                  </Typography>
                  <Typography fontFamily={"sans-serif"} sx={{ display: "flex" }} paddingRight={1}>
                    <Typography fontWeight={"bold"} marginRight={1}>
                      {t("bankaccounts.fields.account_number")}:
                    </Typography>
                    {defaultSupplierBankAccount?.account_number}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex" }}>
                  <Typography fontFamily={"sans-serif"} sx={{ display: "flex" }} width={"55%"} paddingRight={1}>
                    <Typography fontWeight={"bold"} marginRight={1}>
                      {t("bankaccounts.fields.interbank_key")}:
                    </Typography>
                    {defaultSupplierBankAccount?.interbank_key}
                  </Typography>
                  <Typography fontFamily={"sans-serif"} sx={{ display: "flex" }} paddingRight={1}>
                    <Typography fontWeight={"bold"} marginRight={1}>
                      {t("bankaccounts.fields.swift_code")}:
                    </Typography>
                    {defaultSupplierBankAccount?.swift_code}
                  </Typography>
                </Box>
              </Box>
              <Box display={"flex"}>
                <Controller
                  name="supplier_bank_account_id"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) =>
                    <Autocomplete
                      ref={field.ref}
                      size="small"
                      options={comboSupplierBankAccounts}
                      defaultValue={defaultSupplierBankAccount}
                      getOptionLabel={(option) => `${option.account_alias} - ${option.bank_name} - ${option.currency_code}`}
                      renderOption={(props, option: SupplierBankAccount) => (
                        <div key={option.supplier_bank_account_id}>
                          <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                            {option.account_alias} - {option.bank_name} - {option.currency_code}
                          </Box>
                        </div>
                      )}
                      sx={{ width: "100%" }}
                      isOptionEqualToValue={(option, value) => option.supplier_bank_account_id === value.supplier_bank_account_id}
                      onChange={async (_, values) => {
                        field.onChange(values?.supplier_bank_account_id || null);
                        await setDefaultSupplierBankAccount(values ?? undefined);
                        let myCurrency: Currency = {
                          currency_id: 0,
                          currency_code: values?.currency_code ?? "",
                          currency_description: values?.currency_description ?? "",
                          currency_symbol: values?.currency_symbol
                        }
                        await setCurrencySupplier(myCurrency);
                        onChangeTotals(mySelectedRows, values?.currency_code);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`${t("payments.fields.supplierbankaccount")}`}
                          variant="filled"
                          value={field.value}
                          size="small"
                          error={!!errors.supplier_bank_account_id}
                          helperText={GetError(errors.supplier_bank_account_id?.type)}
                        />
                      )}
                      disabled={isEdit}
                    />
                  }
                />
              </Box>
              <Box display={"flex"} paddingTop={1}>
                <Controller
                  name="commission"
                  control={control}
                  rules={{ min: 0.01, pattern: numberWithFourDecimalsRegex, required: hasCommissionAmount }}
                  render={({ field }) => (
                    <TextField
                      variant="filled"
                      label={`${t("payments.fields.commission")}`}
                      ref={field.ref}
                      value={field.value}
                      onChange={async (event) => {
                        const floatValue = parseFloat(event.target.value);
                        await field.onChange(event.target.value);
                        if ((!isNaN(floatValue) && floatValue > 0.000001) || getValues("commission_currency_id")) {
                          setHasCommissionAmount(true);
                        } else {
                          setHasCommissionAmount(false);
                        }
                        if (!getValues("commission_currency_id") && !floatValue) {
                          setHasCommissionAmount(false);
                        }
                        trigger();
                        onChangeTotals(mySelectedRows, currencySupplier?.currency_code);
                      }}
                      error={!!errors.commission}
                      helperText={GetError(errors.commission?.type)}
                      size="small"
                      sx={{
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                        "& input[type=number]": { MozAppearance: "textfield", }, width: "50%", paddingRight: 1
                      }}
                      type="number"
                      disabled={isEdit}
                    />
                  )}
                />
                <Controller
                  name="commission_currency_id"
                  control={control}
                  rules={{ required: hasCommissionAmount }}
                  render={({ field }) =>
                    <Autocomplete
                      ref={field.ref}
                      size="small"
                      options={comboCommissionCurrencies}
                      getOptionLabel={(option) => `${option.currency_code} - ${option.currency_description}`}
                      renderOption={(props, option: Currency) => (
                        <div key={option.currency_id}>
                          <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                            {option.currency_code} - {option.currency_description}
                          </Box>
                        </div>
                      )}
                      sx={{ width: "50%" }}
                      isOptionEqualToValue={(option, value) => option.currency_id === value.currency_id}
                      onChange={async (_, values) => {
                        field.onChange(values?.currency_id || null);
                        let myCurrency: Currency = {
                          currency_id: values?.currency_id ?? 0,
                          currency_code: values?.currency_code ?? "",
                          currency_description: values?.currency_description ?? "",
                          currency_symbol: values?.currency_symbol
                        }
                        setValue("commission_currency_code", values?.currency_code);
                        await setCommissionCurrency(myCurrency);
                        const floatValue = parseFloat(getValues("commission")?.toString() ?? '');
                        if ((floatValue > 0.000001) || isNaN(floatValue)) {
                          setHasCommissionAmount(true);
                        } else {
                          setHasCommissionAmount(false);
                        }
                        if (!values && !getValues("commission_currency_code")) {
                          setHasCommissionAmount(false);
                        }
                        trigger();
                        onChangeTotals(mySelectedRows, currencySupplier?.currency_code);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`${t("payments.fields.commission_currency")}`}
                          variant="filled"
                          value={field.value}
                          size="small"
                          error={!!errors.commission_currency_id}
                          helperText={GetError(errors.commission_currency_id?.type)}
                        />
                      )}
                      disabled={isEdit}
                    />
                  }
                />
              </Box>
            </Box>
          </Box>
          <Box sx={{ width: "1%" }} display={"flex"} justifyContent={"center"}>
            <Divider />
          </Box>
          <Box sx={{ width: "50%" }} className="d-flex custom-card-view">
            <Box className="w-100">
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography fontWeight={"bold"} fontSize={20} marginRight={1}>
                  {t("purchaseinvoices.fields.account_pay")}:
                </Typography>
                <Typography fontFamily={"sans-serif"} sx={{ display: "flex" }} paddingRight={1}>
                  <Typography fontWeight={"bold"} marginRight={1}>
                    {t("bankaccounts.fields.account_alias")}:
                  </Typography>
                  {defaultBankAccountFinal?.account_alias}
                </Typography>
                {/* <Typography fontFamily={"sans-serif"} sx={{ display: "flex" }} paddingRight={1}>
                <Typography fontWeight={"bold"} marginRight={1}>
                  {t("bankaccounts.fields.bank_references")}:
                </Typography>
                {defaultBankAccountFinal?.bank_references}
              </Typography> */}
                <Box sx={{ display: "flex" }}>
                  <Typography fontFamily={"sans-serif"} sx={{ display: "flex" }} width={"50%"} paddingRight={1}>
                    <Typography fontWeight={"bold"} marginRight={1}>
                      {t("bankaccounts.fields.bank_name")}:
                    </Typography>
                    {defaultBankAccountFinal?.bank_name}
                  </Typography>
                  <Typography fontFamily={"sans-serif"} sx={{ display: "flex" }} paddingRight={1}>
                    <Typography fontWeight={"bold"} marginRight={1}>
                      {t("currencies.title-view")}:
                    </Typography>
                    {defaultBankAccountFinal?.currency_description} - {defaultBankAccountFinal?.currency_code}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex" }}>
                  <Typography fontFamily={"sans-serif"} sx={{ display: "flex" }} width={"50%"} paddingRight={1}>
                    <Typography fontWeight={"bold"} marginRight={1}>
                      {t("bankaccounts.fields.card_number")}:
                    </Typography>
                    {defaultBankAccountFinal?.card_number}
                  </Typography>
                  <Typography fontFamily={"sans-serif"} sx={{ display: "flex" }} paddingRight={1}>
                    <Typography fontWeight={"bold"} marginRight={1}>
                      {t("bankaccounts.fields.account_number")}:
                    </Typography>
                    {defaultBankAccountFinal?.account_number}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex" }}>
                  <Typography fontFamily={"sans-serif"} sx={{ display: "flex" }} width={"55%"} paddingRight={1}>
                    <Typography fontWeight={"bold"} marginRight={1}>
                      {t("bankaccounts.fields.interbank_key")}:
                    </Typography>
                    {defaultBankAccountFinal?.interbank_key}
                  </Typography>
                  <Typography fontFamily={"sans-serif"} sx={{ display: "flex" }} paddingRight={1}>
                    <Typography fontWeight={"bold"} marginRight={1}>
                      {t("bankaccounts.fields.swift_code")}:
                    </Typography>
                    {defaultBankAccountFinal?.swift_code}
                  </Typography>
                </Box>
              </Box>
              <Box display={"flex"}>
                <Controller
                  name="intermediary_bank_account_id"
                  control={control}
                  rules={{}}
                  render={({ field }) =>
                    <Autocomplete
                      ref={field.ref}
                      size="small"
                      options={comboBankAccountsGeneral}
                      defaultValue={defaultBankAccountFinal}
                      getOptionLabel={(option) => `${option.account_alias} - ${option.bank_name} - ${option.currency_code}`}
                      renderOption={(props, option: BankAccount) => (
                        <div key={option.bank_account_id}>
                          <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                            {option.account_alias} - {option.bank_name} - {option.currency_code}
                          </Box>
                        </div>
                      )}
                      sx={{ width: "100%" }}
                      isOptionEqualToValue={(option, value) => option.bank_account_id === value.bank_account_id}
                      onChange={async (_, values) => {
                        await field.onChange(values?.bank_account_id || null);
                        setDefaultBankAccountFinal(values ?? undefined);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`${t("payments.fields.finalbankaccount")}`}
                          variant="filled"
                          value={field.value}
                          error={!!errors.intermediary_bank_account_id}
                          helperText={GetError(errors.intermediary_bank_account_id?.type)}
                        />
                      )}
                      disabled={isEdit}
                    />
                  }
                />
                {/* <Controller
                  name="intermediary_bank_references"
                  control={control}
                  rules={{ maxLength: 500 }}
                  render={({ field }) => (
                    <TextField
                      variant="filled"
                      label={`${t("bankaccounts.fields.bank_references")}`}
                      ref={field.ref}
                      value={field.value}
                      onChange={async (event) => {
                        await field.onChange(event.target.value);
                      }}
                      error={!!errors.intermediary_bank_references}
                      helperText={GetError(errors.intermediary_bank_references?.type)}
                      size="small"
                      sx={{ width: "50%" }}
                      disabled={isEdit}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                /> */}
              </Box>
            </Box>
          </Box>
        </Box>
        <Controller
          name="supplier_bank_references"
          control={control}
          rules={{ maxLength: 500 }}
          render={({ field }) => (
            <TextField
              variant="filled"
              label={`${t("bankaccounts.fields.bank_references")}`}
              ref={field.ref}
              value={field.value}
              onChange={async (event) => {
                await field.onChange(event.target.value);
              }}
              error={!!errors.supplier_bank_references}
              helperText={GetError(errors.supplier_bank_references?.type)}
              size="small"
              sx={{ width: "100%", marginTop: 1 }}
              disabled={isEdit}
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
      </Box>
    </form>
  )
}
